import { useMutation } from "@tanstack/react-query";
import { use } from "react";
import {
  ShareCardInput,
  ShareCardRequest,
  ShareCardResponse,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useUserUidNumber } from "../auth/useUserUidNumber.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { NotUniqueError } from "./errors.ts";
import { useCreateCardProjectCache } from "./useCreateCardProjectCache.ts";
import { useRemoveCardFromEditViewsCache } from "./useRemoveCardFromEditViewsCache.ts";

export const useShareCard = () => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const userUidNumber = useUserUidNumber();
  const { displayError, displaySuccess } = use(SnackbarContext);
  const createCardInProjectCache = useCreateCardProjectCache();
  const removeCardFromEditViewsCache = useRemoveCardFromEditViewsCache();
  const { error, isPending, mutate, isError, reset } = useMutation({
    mutationFn: async ({
      cardId,
      responsibleProjectId,
      sharedProjectId,
    }: ShareCardInput): Promise<Response> => {
      const token = await getToken();
      const response = await fetch(
        `/api/projects/${sharedProjectId}/cards/${cardId}/share`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            responsibleProjectId: responsibleProjectId,
          } satisfies ShareCardRequest),
        },
      );

      if (!response.ok) {
        throw new Error("Sharing the card failed");
      }

      return response;
    },
    mutationKey: ["shareCard"],
    onSuccess: async (response) => {
      const parsedResponse = ShareCardResponse.safeParse(await response.json());
      if (!parsedResponse.success) {
        throw new Error("Invalid card ID in response");
      }
      createCardInProjectCache(
        parsedResponse.data,
        parsedResponse.data.responsibleProjectId,
        userUidNumber,
      );
      removeCardFromEditViewsCache(parsedResponse.data.id);
      displaySuccess(translator("changesSaved"));
    },
    onError: (e) => {
      if (e instanceof NotUniqueError) {
        displayError(translator("errorCardEmailNotUnique"));
      } else {
        displayError(translator("errorSavingChanges"));
      }
    },
  });

  return { mutate, isPending, error, isError, reset };
};
